import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, Loader2, Upload } from "lucide-react";
import adminApi, { apiError } from "@/lib/adminApi";

const inputClass =
  "bg-[#050505] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-gold outline-none w-full";

const EMPTY = { name: "", logo_url: "", website_url: "", display_order: 0, is_active: true };

const AdminSponsors = () => {
  const [sponsors, setSponsors] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const logoRef = useRef(null);

  const load = () => adminApi.get("/sponsors").then(({ data }) => setSponsors(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await adminApi.post("/sponsors/logo", fd);
      setForm((f) => ({ ...f, logo_url: data.logo_url }));
      toast.success("Logo importé.");
    } catch (err) {
      toast.error(apiError(err, "Upload impossible."));
    } finally {
      if (logoRef.current) logoRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Le nom du sponsor est obligatoire.");
      return;
    }
    setSaving(true);
    try {
      if (editingId) await adminApi.put(`/sponsors/${editingId}`, form);
      else await adminApi.post("/sponsors", form);
      toast.success(editingId ? "Sponsor mis à jour." : "Sponsor ajouté.");
      setForm(EMPTY);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (s) => {
    try {
      await adminApi.put(`/sponsors/${s.id}`, { name: s.name, logo_url: s.logo_url, website_url: s.website_url, display_order: s.display_order, is_active: !s.is_active });
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (s) => {
    try {
      await adminApi.delete(`/sponsors/${s.id}`);
      toast.success("Sponsor supprimé.");
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div data-testid="admin-sponsors" className="space-y-6">
      <h1 className="font-display uppercase text-4xl tracking-wide">Sponsors / Partenaires</h1>

      <form onSubmit={submit} data-testid="sponsor-form" className="bg-noir-surface border border-noir-border p-6 space-y-4">
        <h2 className="font-display uppercase text-xl tracking-wide text-gold">
          {editingId ? "Modifier le sponsor" : "Ajouter le sponsor"}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <input data-testid="sponsor-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom du sponsor / partenaire *" className={inputClass} />
          <input data-testid="sponsor-website" value={form.website_url} onChange={(e) => setForm({ ...form, website_url: e.target.value })} placeholder="Site internet (https://…)" className={inputClass} />
          <div className="flex items-center gap-3">
            <input ref={logoRef} data-testid="sponsor-logo-upload" id="sponsor-logo-file" type="file" accept=".jpg,.jpeg,.png,.webp" onChange={uploadLogo} className="hidden" />
            <label
              htmlFor="sponsor-logo-file"
              className="inline-flex items-center gap-2 px-4 py-2.5 border border-white/20 text-white/70 text-xs font-bold uppercase tracking-wider rounded-full hover:border-gold hover:text-gold transition-colors cursor-pointer"
            >
              <Upload size={13} /> Logo
            </label>
            {form.logo_url && <img src={form.logo_url} alt="Logo" className="h-9 object-contain border border-white/10 p-1" />}
          </div>
          <input data-testid="sponsor-order" type="number" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: Number(e.target.value) })} placeholder="Ordre d'affichage" className={inputClass} />
          <label className="flex items-center gap-3 text-sm text-white/70 cursor-pointer">
            <input data-testid="sponsor-active" type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="w-4 h-4 accent-[#D4AF37]" />
            Actif (visible publiquement)
          </label>
        </div>
        <div className="flex gap-3">
          <button data-testid="sponsor-submit" disabled={saving} className="inline-flex items-center gap-2 px-6 py-3 bg-rouge text-white text-xs font-bold uppercase tracking-[0.15em] rounded-full hover:bg-rouge-hover disabled:opacity-50">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {editingId ? "Enregistrer" : "Ajouter le sponsor"}
          </button>
          {editingId && (
            <button type="button" onClick={() => { setEditingId(null); setForm(EMPTY); }} className="px-6 py-3 border border-white/20 text-white text-xs font-bold uppercase tracking-[0.15em] rounded-full hover:border-white">
              Annuler
            </button>
          )}
        </div>
      </form>

      <div className="space-y-3">
        {sponsors.map((s) => (
          <div key={s.id} data-testid={`sponsor-row-${s.id}`} className="bg-noir-surface border border-noir-border p-4 flex flex-wrap items-center gap-4">
            <div className="w-28 h-12 flex items-center justify-center bg-black/40 border border-white/5">
              {s.logo_url ? (
                <img src={s.logo_url} alt={s.name} className="max-h-9 object-contain" />
              ) : (
                <span className="font-display text-sm tracking-widest text-white/50">{s.name}</span>
              )}
            </div>
            <div className="flex-1 min-w-[140px]">
              <p className="font-semibold text-sm">{s.name}</p>
              <p className="text-xs text-white/40">Ordre : {s.display_order}{s.website_url ? ` — ${s.website_url}` : ""}</p>
            </div>
            <button
              data-testid={`sponsor-toggle-${s.id}`}
              onClick={() => toggleActive(s)}
              className={`text-[10px] font-bold uppercase tracking-[0.15em] border px-3 py-1.5 transition-colors ${
                s.is_active ? "text-gold border-gold/40" : "text-white/40 border-white/15"
              }`}
            >
              {s.is_active ? "Actif" : "Inactif"}
            </button>
            <button
              data-testid={`sponsor-edit-${s.id}`}
              onClick={() => { setEditingId(s.id); setForm({ name: s.name, logo_url: s.logo_url, website_url: s.website_url, display_order: s.display_order, is_active: s.is_active }); window.scrollTo({ top: 0 }); }}
              className="px-4 py-2 border border-white/15 text-white/60 hover:border-gold hover:text-gold text-xs font-bold uppercase tracking-wider transition-colors"
            >
              Modifier
            </button>
            <button data-testid={`sponsor-delete-${s.id}`} onClick={() => setConfirmDelete(s)} className="w-9 h-9 flex items-center justify-center border border-white/15 text-white/60 hover:border-rouge hover:text-rouge-hover transition-colors">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        {sponsors.length === 0 && <p className="text-white/40 text-sm">Aucun sponsor pour le moment.</p>}
      </div>

      {confirmDelete && (
        <div data-testid="sponsor-delete-confirm" className="fixed inset-0 z-50 bg-noir/90 backdrop-blur flex items-center justify-center p-6">
          <div className="bg-noir-surface border border-rouge/40 p-8 max-w-md w-full">
            <h2 className="font-display uppercase text-2xl tracking-wide text-rouge-hover">Supprimer ce sponsor ?</h2>
            <p className="mt-3 text-white/60 text-sm">Voulez-vous vraiment supprimer <strong className="text-white">{confirmDelete.name}</strong> ? Il disparaîtra de la page publique.</p>
            <div className="mt-6 flex gap-3">
              <button data-testid="sponsor-delete-yes" onClick={() => remove(confirmDelete)} className="px-6 py-3 bg-rouge text-white text-xs font-bold uppercase tracking-[0.15em] rounded-full hover:bg-rouge-hover">
                Supprimer
              </button>
              <button data-testid="sponsor-delete-no" onClick={() => setConfirmDelete(null)} className="px-6 py-3 border border-white/20 text-white text-xs font-bold uppercase tracking-[0.15em] rounded-full hover:border-white">
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSponsors;
